
import React, { useState } from 'react';
import { 
  BarChart3, 
  GraduationCap, 
  ShoppingBag, 
  Code2, 
  Smartphone, 
  Globe, 
  ArrowRight, 
  CheckCircle2, 
  TrendingUp, 
  Users2,
  ChevronLeft,
  AlertCircle,
  XCircle,
  HelpCircle,
  ZapOff,
  Lightbulb,
  ShieldCheck,
  Zap,
  MessageSquareMore,
  ChevronDown
} from 'lucide-react';
import Navbar from './components/Navbar';
import ConsultantAI from './components/ConsultantAI';
import FinancialAppDetail from './components/FinancialAppDetail';
import OnlineShopAppDetail from './components/OnlineShopAppDetail';
import ElearningAppDetail from './components/ElearningAppDetail';
import CustomDevDetail from './components/CustomDevDetail';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<'landing' | 'financial-app' | 'online-shop' | 'elearning' | 'custom-dev'>('landing');

  if (currentView !== 'landing') {
    return (
      <div className="min-h-screen bg-white">
        <nav className="fixed top-0 left-0 right-0 z-50 glass-effect py-4 border-b border-slate-100">
          <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
            <button 
              onClick={() => setCurrentView('landing')}
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors font-medium"
            >
              <ChevronLeft size={20} /> Kembali
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">D</span>
              </div>
              <span className="font-bold text-slate-900">DigiMaju</span>
            </div>
            <div className="w-20"></div> {/* Spacer for alignment */}
          </div>
        </nav>
        {currentView === 'financial-app' && <FinancialAppDetail />}
        {currentView === 'online-shop' && <OnlineShopAppDetail />}
        {currentView === 'elearning' && <ElearningAppDetail />}
        {currentView === 'custom-dev' && <CustomDevDetail />}
        <ConsultantAI />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Navbar />
      
      {/* Hero Section */}
      <section id="hero" className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 -z-10 w-1/2 h-full bg-gradient-to-l from-blue-50 to-transparent rounded-bl-[100px]"></div>
        <div className="container mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
              Partner Digital Pebisnis Indonesia
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.1]">
              Teknologi untuk Membantu Bisnis Anda <span className="text-blue-600 italic">Tumbuh Lebih Cepat</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
              Gunakan aplikasi siap pakai untuk keuangan, elearning, dan toko online, atau kembangkan sistem kustom bersama tim engineer kami—fleksibel untuk UMKM hingga bisnis yang sedang berkembang.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => document.getElementById('produk')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold shadow-xl shadow-blue-200 hover:bg-blue-700 hover:shadow-2xl transition-all flex items-center gap-2"
              >
                Konsultasi Gratis Sekarang <ArrowRight size={20} />
              </button>
              <button className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all">
                Pelajari Solusi Kami
              </button>
            </div>
            <div className="flex items-center gap-8 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => (
                  <img key={i} src={`https://picsum.photos/seed/${i}/100/100`} alt="User" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" />
                ))}
              </div>
              <p className="text-sm text-slate-500">
                Bergabung dengan <span className="font-bold text-slate-800">1,200+</span> Pebisnis lainnya
              </p>
            </div>
          </div>
          <div className="relative animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
            <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl">
              <img src="https://picsum.photos/seed/office/800/1000" alt="UMKM Digital" className="w-full object-cover" />
            </div>
            {/* Floating Cards */}
            <div className="absolute -bottom-6 -left-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4 animate-bounce duration-[3s]">
              <div className="bg-green-100 p-2 rounded-xl text-green-600">
                <TrendingUp size={24} />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 font-medium">Profit UMKM</p>
                <p className="text-sm font-bold text-slate-900">+45% Bulan Ini</p>
              </div>
            </div>
            <div className="absolute top-10 -right-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4 animate-pulse">
              <div className="bg-blue-100 p-2 rounded-xl text-blue-600">
                <Users2 size={24} />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 font-medium">Pelanggan Baru</p>
                <p className="text-sm font-bold text-slate-900">89+ User</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-24 bg-white border-y border-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-16">
            <div className="max-w-2xl space-y-4">
              <h2 className="text-amber-600 font-bold uppercase tracking-wider text-sm">Tantangan Pertumbuhan</h2>
              <h3 className="text-4xl font-bold text-slate-900">Hentikan Hambatan yang Memperlambat Bisnis Anda</h3>
              <p className="text-slate-600 text-lg">Apakah bisnis Anda sering mengalami salah satu masalah di bawah ini? Jika ya, saatnya beralih ke solusi digital.</p>
            </div>
            <div className="hidden md:block">
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                 <HelpCircle className="text-blue-500" />
                 <span className="text-sm font-medium text-slate-700">Bingung mulai dari mana? <a href="#kontak" className="text-blue-600 underline">Diskusi dengan kami</a></span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ProblemCard 
              icon={<ZapOff className="text-amber-500" />}
              title="Keuangan Berantakan"
              description="Catatan manual yang sering hilang, stok tidak akurat, dan tidak tahu pasti apakah bisnis sedang untung atau rugi."
              solution="Aplikasi Keuangan"
            />
            <ProblemCard 
              icon={<XCircle className="text-red-500" />}
              title="Toko Tergantung Marketplace"
              description="Biaya admin marketplace yang tinggi dan brand Anda yang tenggelam di antara ribuan kompetitor lain."
              solution="Aplikasi Toko Online"
            />
            <ProblemCard 
              icon={<AlertCircle className="text-orange-500" />}
              title="Transfer Ilmu Terhambat"
              description="Sulit melatih tim baru atau menskalakan keahlian Anda karena tidak adanya sistem materi yang terpusat."
              solution="Platform E-learning"
            />
            <ProblemCard 
              icon={<Code2 className="text-indigo-500" />}
              title="Sistem Kaku & Tidak Cocok"
              description="Menggunakan software 'seadanya' yang justru menghambat alur kerja unik perusahaan Anda."
              solution="Custom App Dev"
            />
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="produk" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <h2 className="text-blue-600 font-bold uppercase tracking-wider text-sm">Produk Unggulan</h2>
            <h3 className="text-4xl font-bold text-slate-900">Ekosistem Digital untuk UMKM dan Bisnis Berkembang</h3>
            <p className="text-slate-600">Beragam solusi untuk mempercepat pertumbuhan bisnis Anda.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <ProductCard 
              icon={<BarChart3 className="text-blue-600" size={32} />}
              title="Aplikasi Keuangan UMKM"
              description="Catat transaksi, kelola stok, dan pantau laba-rugi secara real-time dari ponsel Anda."
              features={["Laporan Keuangan Otomatis", "Manajemen Stok", "Analisis Tren Penjualan"]}
              onDetailClick={() => setCurrentView('financial-app')}
            />
            <ProductCard 
              icon={<GraduationCap className="text-purple-600" size={32} />}
              title="Platform E-Learning"
              description="Pelajari strategi bisnis digital dari para ahli melalui video interaktif dan kelas online."
              features={["Kurikulum Bisnis Praktis", "Mentoring Langsung", "Sertifikasi Digital"]}
              onDetailClick={() => setCurrentView('elearning')}
            />
            <ProductCard 
              icon={<ShoppingBag className="text-orange-600" size={32} />}
              title="Aplikasi Toko Online"
              description="Buka toko online mandiri dalam hitungan menit. Terintegrasi payment gateway dan logistik."
              features={["Situs Web Sendiri", "Integrasi Kurir", "WhatsApp Checkout"]}
              onDetailClick={() => setCurrentView('online-shop')}
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="layanan" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div className="space-y-4 pt-12">
                <img src="https://picsum.photos/seed/webdev/400/500" alt="Web Dev" className="rounded-2xl shadow-lg" />
                <img src="https://picsum.photos/seed/mobile/400/300" alt="Mobile Dev" className="rounded-2xl shadow-lg" />
              </div>
              <div className="space-y-4">
                <img src="https://picsum.photos/seed/design/400/300" alt="Design" className="rounded-2xl shadow-lg" />
                <img src="https://picsum.photos/seed/team/400/500" alt="Team" className="rounded-2xl shadow-lg" />
              </div>
            </div>
            <div className="flex-1 space-y-8">
              <h2 className="text-blue-600 font-bold uppercase tracking-wider text-sm">Jasa Pengembangan</h2>
              <h3 className="text-4xl font-bold text-slate-900">Solusi Kustom untuk Kebutuhan Unik Anda</h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Kami tidak hanya memberikan produk jadi, tetapi juga membantu Anda membangun sistem yang dirancang khusus sesuai alur kerja perusahaan Anda.
              </p>
              
              <div className="space-y-4">
                <ServiceItem 
                  icon={<Globe className="text-blue-500" />}
                  title="Web Development"
                  description="Dari Company Profile hingga Website kompleks yang scalable."
                />
                <ServiceItem 
                  icon={<Smartphone className="text-indigo-500" />}
                  title="Mobile App Development"
                  description="Aplikasi iOS & Android dengan performa native dan UI modern."
                />
                <ServiceItem 
                  icon={<Code2 className="text-cyan-500" />}
                  title="UI/UX Design"
                  description="Desain antarmuka yang intuitif dan berorientasi pada pengguna."
                />
              </div>

              <button 
                onClick={() => setCurrentView('custom-dev')}
                className="px-8 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all"
              >
                Konsultasi Gratis Sekarang
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Consult Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-600/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/20 text-blue-400 rounded-full text-sm font-bold uppercase tracking-wider">
                <MessageSquareMore size={16} /> Strategic Value
              </div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">Mengapa Konsultasi dengan <span className="text-blue-500">DigiMaju</span> Adalah Langkah Tepat?</h2>
              <p className="text-slate-400 text-lg">Dapatkan rekomendasi dan peta jalan digital khusus untuk pertumbuhan yang berkelanjutan bagi bisnis Anda, tanpa komitmen apapun.</p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                 <ConsultBenefit 
                    icon={<Lightbulb className="text-yellow-400" />}
                    title="Fokus pada Kebutuhan Bisnis"
                    desc="Kami mulai dengan memahami proses bisnis, tantangan, dan tujuan Anda agar solusi yang diberikan benar-benar relevan"
                 />
                 <ConsultBenefit 
                    icon={<Zap className="text-blue-400" />}
                    title="Rekomendasi Solusi yang Tepat"
                    desc="Setelah memahami kebutuhan Anda, kami akan merekomendasikan pendekatan terbaik untuk kebutuhan bisnis Anda"
                 />
                 <ConsultBenefit 
                    icon={<ShieldCheck className="text-green-400" />}
                    title="Diskusi Santai & Fleksibel tanpa Komitmen"
                    desc="Konsultasi dapat dimulai melalui WhatsApp dan dilanjutkan ke diskusi lebih lanjut jika diperlukan. Sesi awal biasanya berlangsung sekitar 15–30 menit"
                 />
                 <ConsultBenefit 
                    icon={<Users2 className="text-purple-400" />}
                    title="Berpengalaman Membangun Berbagai Aplikasi"
                    desc="Kami telah membantu berbagai bisnis dalam digitalisasi operasional, pengembangan platform online, dan sistem internal. Kami juga bersemangat untuk membantu bisnis Anda"
                 />
              </div>
            </div>
            <div className="relative">
               <div className="bg-white/5 p-8 md:p-12 rounded-[3rem] border border-white/10 backdrop-blur-sm space-y-8">
                  <div className="space-y-4">
                     <h4 className="text-2xl font-bold">Tersedia Sesi Konsultasi Gratis</h4>
                     <p className="text-slate-400">Dalam konsultasi awal, Anda bisa mendapatkan rekomendasi solusi berdasarkan kebutuhan bisnis Anda hingga pemilihan teknologi untuk solusi</p>
                  </div>
                  <div className="space-y-4">
                     <button className="w-full py-5 bg-blue-600 text-white rounded-2xl font-bold text-lg hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/20 transition-all flex items-center justify-center gap-3">
                        Konsultasi Gratis via WhatsApp <ArrowRight size={22} />
                     </button>
                  </div>
                  <div className="pt-4 flex items-center justify-center gap-8 border-t border-white/10">
                     <div className="text-center">
                        <p className="text-2xl font-bold text-white">100%</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Gratis</p>
                     </div>
                     <div className="w-px h-8 bg-white/10"></div>
                     <div className="text-center">
                        <p className="text-2xl font-bold text-white">30m</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Durasi</p>
                     </div>
                     <div className="w-px h-8 bg-white/10"></div>
                     <div className="text-center">
                        <p className="text-2xl font-bold text-white">0%</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Komitmen</p>
                     </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4 space-y-6">
              <h2 className="text-blue-600 font-bold uppercase tracking-wider text-sm">FAQ</h2>
              <h3 className="text-4xl font-bold text-slate-900 leading-tight">Pertanyaan yang Sering Diajukan</h3>
              <p className="text-slate-600 text-lg">Temukan jawaban cepat untuk pertanyaan yang paling umum kami terima terkait solusi digital DigiMaju.</p>
              <div className="pt-6">
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                  <p className="font-bold text-slate-900">Masih punya pertanyaan?</p>
                  <p className="text-sm text-slate-500">Tim kami siap membantu Anda secara langsung via WhatsApp atau Email.</p>
                  <button className="flex items-center gap-2 text-blue-600 font-bold text-sm hover:gap-3 transition-all">
                    Hubungi Support <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 space-y-4">
              <FAQItem 
                question="Apakah konsultasi awal gratis?" 
                answer="Konsultasi awal gratis untuk memahami kebutuhan bisnis Anda. Tidak dikenakan biaya dan tanpa komitmen"
              />
              <FAQItem 
                question="Apakah saya harus langsung membeli produk atau layanan setelah konsultasi?" 
                answer="Tidak ada kewajiban untuk membeli produk atau layanan kami setelah sesi konsultasi berakhir. Konsultasi bertujuan membantu Anda mengeksplorasi solusi dan opsi terlebih dahulu"
              />
              <FAQItem 
                question="Apakah produk dan layanan hanya untuk UMKM?" 
                answer="Kami membantu UMKM hingga bisnis yang sedang berkembang dengan kebutuhan sistem dan aplikasi digital"
              />
              <FAQItem 
                question="Apakah bisa membuat sistem custom?" 
                answer="Ya, kami dapat mengembangkan sistem sesuai kebutuhan bisnis Anda mulai dari aplikasi website hingga mobile"
              />
              <FAQItem 
                question="Berapa kisaran biaya pengembangan?" 
                answer="Biaya bergantung pada kebutuhan dan kompleksitas fitur. Kami dapat memberikan gambaran awal setelah konsultasi"
              />
              <FAQItem 
                question="Bagaimana cara memulai?" 
                answer="Cukup hubungi kami via WhatsApp untuk diskusi singkat mengenai kebutuhan bisnis Anda"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Empowerment Banner */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="bg-blue-600 rounded-[3rem] p-12 md:p-20 relative overflow-hidden text-center text-white">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
               <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
               </svg>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Misi Kami: 1 Juta Pebisnis Go-Digital</h2>
            <p className="text-blue-100 text-lg md:text-xl max-w-3xl mx-auto mb-10">
              Kami percaya bahwa teknologi harus inklusif. Kami hadir untuk menjembatani kesenjangan digital dan membantu pengusaha lokal bersaing di pasar global.
            </p>
            <div className="flex flex-col md:flex-row justify-center items-center gap-12">
              <StatItem label="UMKM Terdaftar" value="1.2k+" />
              <StatItem label="Proyek Selesai" value="250+" />
              <StatItem label="Kepuasan Klien" value="98%" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="kontak" className="bg-slate-900 text-slate-300 pt-20 pb-10">
        <div className="container mx-auto px-4 md:px-8 grid md:grid-cols-4 gap-12 border-b border-slate-800 pb-16 mb-10">
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">D</span>
              </div>
              <span className="text-xl font-bold tracking-tight text-white">
                Digi<span className="text-blue-600">Maju</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed">
              Membangun masa depan digital Indonesia melalui pemberdayaan UMKM dengan solusi teknologi cerdas dan terjangkau.
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Produk</h4>
            <ul className="space-y-4 text-sm">
              <li><a href="#" className="hover:text-blue-400 transition-colors">Keuangan UMKM</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors">Platform E-learning</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors">Toko Online</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Layanan</h4>
            <ul className="space-y-4 text-sm">
              <li><a href="#" className="hover:text-blue-400 transition-colors">Web Development</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors">Mobile Development</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors">Maintenance & Support</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Kontak Kami</h4>
            <ul className="space-y-4 text-sm">
              <li>hello@digimaju.id</li>
              <li>+62 812-3456-7890</li>
              <li>Jakarta Selatan, Indonesia</li>
            </ul>
          </div>
        </div>
        <div className="container mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <p>© 2025 DigiMaju Indonesia. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white transition-colors">Kebijakan Privasi</a>
            <a href="#" className="hover:text-white transition-colors">Syarat & Ketentuan</a>
          </div>
        </div>
      </footer>

      <ConsultantAI />
    </div>
  );
};

const FAQItem: React.FC<{ question: string, answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`border rounded-2xl transition-all duration-300 ${isOpen ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-slate-100 hover:border-slate-200 shadow-sm'}`}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-6 flex justify-between items-center gap-4"
      >
        <span className="font-bold text-slate-900 leading-tight">{question}</span>
        <div className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}>
          <ChevronDown size={20} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="p-6 pt-0 text-slate-600 text-sm leading-relaxed border-t border-blue-100/50">
          {answer}
        </div>
      </div>
    </div>
  );
};

const ConsultBenefit: React.FC<{ icon: React.ReactNode, title: string, desc: string }> = ({ icon, title, desc }) => (
   <div className="space-y-3 p-4 rounded-2xl hover:bg-white/5 transition-colors">
      <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg">
         {icon}
      </div>
      <h4 className="font-bold text-white">{title}</h4>
      <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
   </div>
);

const ProblemCard: React.FC<{ icon: React.ReactNode, title: string, description: string, solution: string }> = ({ icon, title, description, solution }) => (
  <div className="p-6 rounded-2xl border border-slate-100 bg-white hover:border-amber-200 hover:shadow-lg transition-all group">
    <div className="mb-4 bg-slate-50 p-3 rounded-xl w-fit group-hover:bg-amber-50 transition-colors">
      {icon}
    </div>
    <h4 className="text-lg font-bold text-slate-900 mb-3">{title}</h4>
    <p className="text-sm text-slate-500 mb-6 leading-relaxed">{description}</p>
    <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Solusi DigiMaju</span>
      <span className="text-xs font-bold text-blue-600">{solution}</span>
    </div>
  </div>
);

const ProductCard: React.FC<{ icon: React.ReactNode, title: string, description: string, features: string[], onDetailClick?: () => void }> = ({ icon, title, description, features, onDetailClick }) => (
  <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
    <div className="mb-6 p-3 bg-slate-50 rounded-2xl w-fit group-hover:bg-white group-hover:shadow-md transition-all">
      {icon}
    </div>
    <h4 className="text-xl font-bold text-slate-900 mb-4">{title}</h4>
    <p className="text-slate-600 mb-6 text-sm leading-relaxed">{description}</p>
    <ul className="space-y-3">
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
          <CheckCircle2 size={16} className="text-green-500 shrink-0" /> {f}
        </li>
      ))}
    </ul>
    <button 
      onClick={onDetailClick}
      className="mt-8 w-full py-3 bg-blue-50 text-blue-600 rounded-xl font-bold hover:bg-blue-600 hover:text-white transition-all"
    >
      Lihat Detail
    </button>
  </div>
);

const ServiceItem: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-colors">
    <div className="shrink-0 mt-1">
      {icon}
    </div>
    <div>
      <h4 className="font-bold text-slate-900">{title}</h4>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
  </div>
);

const StatItem: React.FC<{ label: string, value: string }> = ({ label, value }) => (
  <div className="space-y-2">
    <div className="text-4xl md:text-5xl font-extrabold">{value}</div>
    <div className="text-blue-100 font-medium tracking-wide">{label}</div>
  </div>
);

export default App;
